import React from 'react';
import { Box, Typography, TextField, InputAdornment } from '@mui/material';
import { LocationOn } from '@mui/icons-material';
import { formatZipCode, validateZipCode, isZipComplete } from '../../../utils/inputFormatters';

const TextQuestion = ({ 
  question, 
  value = '', 
  onChange, 
  interpolateText, 
  answers, 
  error,
  type = 'text'
}) => {
  // Check if this is a zip code question
  const isZipCode = question.validation === 'zipCode' || 
                   question.id === 'zipCode' || 
                   question.placeholder?.toLowerCase().includes('zip');
  
  const handleChange = (event) => {
    let processedValue = event.target.value;
    
    // Apply zip code formatting if this is a zip code field
    if (isZipCode) {
      processedValue = formatZipCode(processedValue);
    }
    
    onChange(processedValue);
  };

  const handleKeyDown = (event) => {
    if (isZipCode) {
      // Allow backspace, delete, tab, escape, enter, and arrow keys
      if ([8, 9, 27, 13, 46, 37, 38, 39, 40].indexOf(event.keyCode) !== -1 ||
          // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
          (event.keyCode === 65 && event.ctrlKey) ||
          (event.keyCode === 67 && event.ctrlKey) ||
          (event.keyCode === 86 && event.ctrlKey) ||
          (event.keyCode === 88 && event.ctrlKey)) {
        return;
      }
      
      // Ensure that it is a number and stop the keypress if it's not
      if ((event.shiftKey || (event.keyCode < 48 || event.keyCode > 57)) && (event.keyCode < 96 || event.keyCode > 105)) {
        event.preventDefault();
      }
      
      // Stop input if we already have 5 digits
      if (value.length >= 5 && ![8, 46].includes(event.keyCode)) {
        event.preventDefault();
      }
    }
  };

  // Enhanced validation and styling for zip codes
  const zipError = isZipCode ? (error || validateZipCode(value)) : error;
  const isZipCompleteCheck = isZipCode ? isZipComplete(value) : false;
  const effectiveError = isZipCode ? zipError : error;

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600, whiteSpace: 'pre-line' }}>
        {interpolateText(question.text, answers)}
      </Typography>
      
      <TextField
        fullWidth
        type={isZipCode ? 'text' : type}
        inputMode={isZipCode ? 'numeric' : undefined}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={question.placeholder || (isZipCode ? '12345' : 'Enter your answer...')}
        variant="outlined"
        size="large"
        error={!!effectiveError}
        helperText={effectiveError || (isZipCode && isZipCompleteCheck ? '✓ ZIP code complete' : 
                    isZipCode ? 'Enter your 5-digit ZIP code' : undefined)}
        autoFocus
        InputProps={isZipCode ? {
          startAdornment: (
            <InputAdornment position="start">
              <LocationOn sx={{ color: isZipCompleteCheck ? 'success.main' : 'action.active' }} />
            </InputAdornment>
          ),
        } : undefined}
        sx={{
          '& .MuiOutlinedInput-root': {
            fontSize: '1.1rem',
            padding: '12px 14px',
            ...(isZipCode && isZipCompleteCheck && {
              '& fieldset': {
                borderColor: 'success.main',
              },
              '&:hover fieldset': {
                borderColor: 'success.main',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'success.main',
              }
            })
          },
          ...(isZipCode && isZipCompleteCheck && !effectiveError && {
            '& .MuiFormHelperText-root': {
              color: 'success.main',
              fontWeight: 500
            }
          })
        }}
      />
      
      {isZipCode && isZipCompleteCheck && !effectiveError && (
        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
          We'll use this to provide accurate service pricing for your area.
        </Typography>
      )}
    </Box>
  );
};

export default TextQuestion;